import * as React from "react";
import { UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { ILOBCheckbox, TypeMenuProps } from "../typings";
import { UitkPill } from "uitk-react-pill";
import { FlightTypeAdaptiveMenu } from "components/flexComponents/WizardFlightPWA/components/FlightTypeAdaptiveMenu";
import { FlightType } from "stores/wizard/state/typings";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { Experiment as ExperimentList, isVariantEnabled } from "components/utility/experiment";
import { useLocalization } from "@shared-ui/localization-context";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

interface TypeMenuPillsProps extends TypeMenuProps {
  showMulticity: boolean;
  showOneway: boolean;
  setFlightType: () => string;
  onFlightTypeUpdate: (flightType: FlightType) => void;
}

const shouldShowDisplayEnhancedLabels = (context: ExtendedContextStore) => {
  const allowedVariants = [2, 3, 4];

  return allowedVariants.some((variant) =>
    isVariantEnabled(context, ExperimentList.PWA_Package_Wizard_Multi_LOB, variant)
  );
};

export const TypeMenuPills = ({
  handleMenuChange,
  onFlightTypeUpdate,
  lobsMap,
  setFlightType,
  context,
  showMulticity,
  showOneway,
  className,
  useLink,
  lobKeys,
}: TypeMenuPillsProps) => {
  const flightsMapKey = "flights";
  const { formatText } = useLocalization();
  const displayEnhancedLabels = shouldShowDisplayEnhancedLabels(context);
  /**
   * Handle flight type selection when adaptive menu is available
   * See: https://abacus.prod.expedia.com/experiment/38435
   */
  const onSelectFlightType = (flighType: FlightType) => {
    const { ref, id } = lobsMap.flights;
    const pillState = true;

    handleMenuChange(flightsMapKey, ref, id, pillState);
    onFlightTypeUpdate(flighType);
  };

  /** Unselect flight pill when adaptive menu clear button is clicked */
  const unselectFlightPill = () => {
    const { ref, id } = lobsMap.flights;
    const pillState = false;

    handleMenuChange(flightsMapKey, ref, id, pillState);
  };

  const renderFlightPillWithAdaptiveMenu = () => {
    const { checked, label: pillLabel } = lobsMap.flights;
    const currentFlightType = setFlightType();
    const label = checked ? `${pillLabel}: ${currentFlightType}` : pillLabel;
    const flightTypeSelectorInPillExperimentName = "newFlightsinPill";

    return (
      <Experiment name={flightTypeSelectorInPillExperimentName} key={flightsMapKey}>
        <ExperimentControl>{renderPill(flightsMapKey, lobsMap.flights)}</ExperimentControl>
        <ExperimentVariant bucket={1}>
          <FlightTypeAdaptiveMenu
            onSelectFlightType={onSelectFlightType}
            onClearButtonClick={unselectFlightPill}
            initialFlightType={currentFlightType}
            trigger={
              <UitkPill
                id={`package-pills-${flightsMapKey}`}
                toggleState={checked}
                children={label}
                triggerPill={true}
              />
            }
            showMulticity={showMulticity}
            showOneway={showOneway}
          />
        </ExperimentVariant>
      </Experiment>
    );
  };

  const renderPill = (key: string, { checked, ref, id, label, enhancedLabel }: ILOBCheckbox) => {
    const overridenLabel = checked && displayEnhancedLabels ? enhancedLabel : label;

    const onChangePill = () => {
      handleMenuChange(key, ref, id);
    };

    return (
      <UitkPill
        key={key}
        id={!useLink ? `package-pills-${key}` : ""}
        checked={checked}
        onChange={onChangePill}
        children={overridenLabel}
      />
    );
  };

  const checkLOBAddedExperimentLabels = () => {
    const hotelLabel = formatText("wizard.packages.stay.pill.text.variant");
    const flightsLabel = formatText("wizard.packages.flight.pill.text.variant");
    const carLabel = formatText("wizard.packages.car.pill.text.variant");

    lobsMap.cars.label = carLabel;
    lobsMap.hotels.label = hotelLabel;
    lobsMap.flights.label = flightsLabel;
  };

  const pillsToLoad = () => {
    if (displayEnhancedLabels) {
      checkLOBAddedExperimentLabels();
    }

    return lobKeys.map((lobKey) => {
      const lobCheckbox: ILOBCheckbox = lobsMap[lobKey];
      const pillToLoad = renderPill(lobKey, lobCheckbox);

      if (flightsMapKey === lobKey && (showMulticity || showOneway)) {
        return renderFlightPillWithAdaptiveMenu();
      }

      if (useLink) {
        return (
          <button key={lobKey} className="all-x-margin-one ssr-link-pill">
            {pillToLoad}
          </button>
        );
      }

      return pillToLoad;
    });
  };

  return (
    <UitkLayoutFlexItem className={className}>
      <div>
        <div data-testid="type-menu-pills">{pillsToLoad()}</div>
      </div>
    </UitkLayoutFlexItem>
  );
};
